import React from 'react';
import { Button, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../../../utils/logger';
import { NS, en } from '../../i18n';
import './DashboardErrorBoundary.css';

const logger = createLogger('dashboards');

interface DashboardErrorBoundaryProps {
  children: React.ReactNode;
}

interface DashboardErrorBoundaryState {
  hasError: boolean;
}

/**
 * The fallback's copy, in the app's language.
 *
 * A function component of its own so it can use `useTranslation`, which also
 * re-renders it when the language changes while the fallback is on screen. It is
 * a sibling of the tree that failed, not part of it, so the hook is not what
 * broke. The English bundle is the `defaultValue`: should the namespace not be
 * registered, the fallback still reads as a sentence and never as raw keys.
 */
const DashboardErrorFallback: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const { t } = useTranslation(NS);
  return (
    <div className="dashboard-error-boundary" role="alert">
      <Result
        status="warning"
        title={t('errorBoundary.title', { defaultValue: en.errorBoundary.title })}
        subTitle={t('errorBoundary.body', { defaultValue: en.errorBoundary.body })}
        extra={
          <Button type="primary" onClick={onRetry}>
            {t('errorBoundary.retry', { defaultValue: en.errorBoundary.retry })}
          </Button>
        }
      />
    </div>
  );
};

/**
 * Scopes runtime errors to the dashboard editor. If a section, the canvas or a
 * widget throws during render, the fallback replaces this feature only — the rest
 * of the app keeps running instead of crashing to a blank screen. "Try again"
 * resets the boundary so a transient error can recover without a full reload.
 *
 * It is worth having here and not only in `agent-setup`: this feature grows into
 * a canvas of user-configured widgets fed by user data (SL-1662..SL-1665), which
 * is exactly the shape of code that throws on an unexpected value.
 *
 * No external dependency: the repo has no shared ErrorBoundary and doesn't bundle
 * `react-error-boundary`, so this is a minimal local class component — the same
 * one `AgentSetupErrorBoundary` settled on.
 */
export class DashboardErrorBoundary extends React.Component<
  DashboardErrorBoundaryProps,
  DashboardErrorBoundaryState
> {
  state: DashboardErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): DashboardErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Through the shared logger, not `console.error`: the definition of done in
    // `governance/dev-rules.yml` forbids new console calls, and the logger is
    // what respects the environment's log level.
    logger.error('render error:', error, info.componentStack);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <DashboardErrorFallback onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}
